import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditMove } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PatchModel } from "@shared/models/patch"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { format, parseISO } from "date-fns"
import { compact, flatMap, map, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"
import { BUMP_RANGE_TO_DATE } from "./BUMP_RANGE_TO_DATE"

interface Payload {
  courses: Array<Course>
  startDate: string
  endDate: string
  /** Only used by the analytics events. */
  dateStrings: string[]
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export function prepareBumpAllLessonsInRangeToDate(payload: Payload): PreparedAction {
  return {
    name: "BUMP_ALL_LESSONS_IN_RANGE_TO_DATE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

export function patches(payload: Payload): Array<PatchModel> {
  return flatMap(payload.courses, (course) => {
    return BUMP_RANGE_TO_DATE.patches({
      course: course,
      startDate: payload.startDate,
      endDate: payload.endDate,
      planbook: payload.planbook,
      rotationCalendar: payload.rotationCalendar,
    })
  })
}
export function undoPatches(payload: Payload): Array<PatchModel> {
  return flatMap(payload.courses, (course) => {
    return BUMP_RANGE_TO_DATE.undoPatches({
      course: course,
      startDate: payload.startDate,
      endDate: payload.endDate,
      planbook: payload.planbook,
      rotationCalendar: payload.rotationCalendar,
    })
  })
}

export function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let planbookId = payload.planbook.id
  let activityToValues = map(payload.courses, (course) => `course-edits:course-${course.id}`)
  activityToValues.push(`planbook-edits:planbook-${planbookId}`)

  let courseCount = payload.courses.length
  let formattedStartDateString = format(parseISO(payload.startDate), "EEE, LLL. d, yyyy")
  let formattedEndDateString = format(parseISO(payload.endDate), "EEE, LLL. d, yyyy")
  let analyticsEvents = payload.dateStrings.flatMap((dateString) =>
    pipe(
      payload.courses,
      A.filter((course) => {
        let courseDate = findCourseDateCustom(course, dateString)
        return (
          courseDate?.attributes.cardStackId !== undefined &&
          courseDate.attributes.cardStackId !== null
        )
      }),
      A.map((course): LessonEditMove | null => {
        let courseDate = findCourseDateCustom(course, dateString)
        let cardStackId = courseDate?.attributes.cardStackId
        if (cardStackId === undefined || cardStackId === null) return null
        let models = defaultLessonModels(
          { cardStackId: cardStackId, planbookId: payload.planbook.id },
          findFn,
          userId
        )
        return {
          ...createBlankEvent(),
          root_id: payload.planbook.id,
          root_type: "planbook",
          date_plan_scheduled_for: models.dateStringForCardStack,
          primary_object_id: cardStackId,
          primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
          primary_verb: "move",
          primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
          primary_object_type: "lesson",
          primary_object_specifier: null,

          adverbial_verb: "bumping",
          adverbial_object_type: "day",
          adverbial_object_specifier: "forward",
          adverbial_object_id: dateString,
          adverbial_possessive: null,
          adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
        }
      }),
      (arr) => compact(arr)
    )
  )
  return {
    title: "Bumped Lessons Forward for Date Range",
    titleForAnalytics: "Day - Bumped",
    description:
      `Moved ${courseCount} lessons forward on ` + payload.endDate === payload.startDate
        ? formattedStartDateString
        : `${formattedStartDateString} - ${formattedEndDateString}`,
    context: {
      courseIds: map(payload.courses, "id"),
      planbookTitle: payload.planbook.attributes.title,
      planbookId: payload.planbook.id,
    },
    activity: {
      actor: `user-${userId}`,
      verb: "edited",
      object: `planbook-${planbookId}`,
      to: activityToValues,
    },
    newAnalyticsEvents: analyticsEvents,
  }
}
